/**
 * TripDetailPage - View a trip's daily itinerary
 * Shows trip header with navigation back, day navigator, and day view
 * Desktop: sidebar + two-column layout (timeline + map/summary panel)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Share2, Plus, ArrowLeft, Map, PersonStanding, Lightbulb, MapPin, Clipboard, CalendarPlus, MoreVertical, Trash2 } from 'lucide-react';
import { apiGet, apiPost } from '../services/apiClient';
import { itineraryService } from '../services/itineraryService';
import { generateDaysFromBackend, addDays, formatDateRange, formatDateShort, formatDateWithDay } from '../utils/dates';
import { convertUnplannedItineraries } from '../utils/itineraryConverter';
import { getDaySortKey } from '../utils/activityHelpers';
import { REALTIME_EVENTS } from '../utils/realtimeEvents';
import { getPlacePhotoUrl } from '../utils/photoUrl';
import type { Day, Trip, TripDetailDTO, TripDetailResponse } from '../types/trip';
import type { CreateActivityInput, Activity, ActivityType } from '../types/activity';
import ActivityCard from '../components/activity/ActivityCard';
import ActivityList from '../components/activity/ActivityList';
import ActivityTypeFilter from '../components/activity/ActivityTypeFilter';
import Modal from '../components/common/Modal';
import ActivityForm from '../components/activity/ActivityForm';
import type { ActivityFormHandle } from '../components/activity/ActivityForm';
import ShareDialog from '../components/trips/ShareDialog';
import MapBottomSheet from '../components/trips/MapBottomSheet';
import TripMapView from '../components/trips/TripMapView';
import DesktopSidebar from '../components/common/DesktopSidebar';
import LoadingScreen from '../components/common/LoadingScreen';
import TripMenuSheet from '../components/trips/TripMenuSheet';
import Dropdown from '../components/common/Dropdown';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useTripRealtimeSubscribe } from '../contexts/TripRealtimeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { getActivityTypeConfig } from '../utils/activityTypes';


/** Compute total duration in hours from activities with start/end times */
function computeDuration(activities: Activity[]): string {
  let totalMinutes = 0;
  for (const a of activities) {
    if (a.startTime && a.endTime) {
      const [sh, sm] = a.startTime.split(':').map(Number);
      const [eh, em] = a.endTime.split(':').map(Number);
      const diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff > 0) totalMinutes += diff;
    }
  }
  if (totalMinutes === 0) return '—';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m > 0 ? `${h} 小時 ${m} 分` : `${h} 小時`;
}


export default function TripDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDayParam = searchParams.get('day');
  const [selectedDayIndex, setSelectedDayIndex] = useState(
    initialDayParam ? parseInt(initialDayParam, 10) - 1 : -1
  );
  const [trip, setTrip] = useState<Trip | undefined>();
  const [tripDetail, setTripDetail] = useState<TripDetailDTO | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | undefined>();
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | undefined>();
  const [editingActivity, setEditingActivity] = useState<Activity | undefined>();
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [overviewMapScope, setOverviewMapScope] = useLocalStorageState<'all' | 'unscheduled'>('tripway:overview-map-scope', 'all');
  const [overviewMapActivityId, setOverviewMapActivityId] = useState<string | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)'); // 對齊 Tailwind lg: 斷點
  // 存在 localStorage：進行程詳情再返回時頁面會重新掛載，用 useState 會被重設回開啟
  const [showMapRoute, setShowMapRoute] = useLocalStorageState('tripway:map-show-route', true);
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [unscheduledTypeFilter, setUnscheduledTypeFilter] = useState<ActivityType[]>([]);
  const [dayTypeFilter, setDayTypeFilter] = useState<ActivityType[]>([]);
  const [deletingActivity, setDeletingActivity] = useState<Activity | undefined>();
  const hasFetchedRef = useRef<string | null>(null);
  const activityFormRef = useRef<ActivityFormHandle>(null);

  useEffect(() => {
    setSelectedActivityId(null);
  }, [selectedDayIndex]);

  // 把目前選中的天數同步進網址，讓從行程詳情頁返回時能回到原本那一天，而不是重置回總覽
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedDayIndex >= 0) {
          next.set('day', String(selectedDayIndex + 1));
        } else {
          next.delete('day');
        }
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDayIndex]);

  useEffect(() => {
    if (!id) return;
    if (hasFetchedRef.current === id) return;

    const loadTrip = async () => {
      try {
        setIsLoading(true);
        hasFetchedRef.current = id;
        const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
        if (response.exitCode === 1 && response.data) {
          const tripDetailData = response.data;
          setTrip({
            id: String(tripDetailData.tripId),
            name: tripDetailData.name,
            startDate: new Date(tripDetailData.startDate),
            endDate: new Date(tripDetailData.endDate),
            userId: String(tripDetailData.userId),
            tags: [],
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          setTripDetail(tripDetailData);
        } else {
          navigate('/trips');
        }
      } catch (error) {
        console.error('TripDetailPage: Failed to load trip:', error);
        hasFetchedRef.current = null;
        navigate('/trips');
      } finally {
        setIsLoading(false);
      }
    };

    loadTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refetchTripDetail = useCallback(async () => {
    if (!id) return;
    const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
    if (response.exitCode === 1 && response.data) setTripDetail(response.data);
  }, [id]);

  useTripRealtimeSubscribe(REALTIME_EVENTS.ITINERARY_ADDED, refetchTripDetail);
  useTripRealtimeSubscribe(REALTIME_EVENTS.ITINERARY_UPDATED, refetchTripDetail);
  useTripRealtimeSubscribe(REALTIME_EVENTS.ITINERARY_MOVED, refetchTripDetail);
  useTripRealtimeSubscribe(REALTIME_EVENTS.ITINERARY_DELETED, refetchTripDetail);
  useTripRealtimeSubscribe(REALTIME_EVENTS.RECONNECTED, refetchTripDetail);

  const days: Day[] = useMemo(() => (tripDetail ? generateDaysFromBackend(tripDetail) : []), [tripDetail]);

  const unscheduledActivities = useMemo(() => {
    if (!tripDetail || !trip) return [];
    return convertUnplannedItineraries(tripDetail.unplannedItineraries, trip.id);
  }, [tripDetail, trip]);

  const filteredUnscheduledActivities = useMemo(() => {
    if (unscheduledTypeFilter.length === 0) return unscheduledActivities;
    return unscheduledActivities.filter((a) => a.type && unscheduledTypeFilter.includes(a.type));
  }, [unscheduledActivities, unscheduledTypeFilter]);

  // Overview 地圖：預設顯示整趟旅程所有行程，可切換成只看未排期；類型篩選同步套用
  const overviewMapActivities = useMemo(() => {
    const base = overviewMapScope === 'all'
      ? [...days.flatMap((d) => d.activities), ...unscheduledActivities]
      : unscheduledActivities;
    if (unscheduledTypeFilter.length === 0) return base;
    return base.filter((a) => a.type && unscheduledTypeFilter.includes(a.type));
  }, [overviewMapScope, days, unscheduledActivities, unscheduledTypeFilter]);

  const selectedDay = selectedDayIndex >= 0 ? days[selectedDayIndex] : undefined;

  const sortedDayActivities = useMemo(() => {
    if (!selectedDay) return [];
    return [...selectedDay.activities].sort((a, b) =>
      getDaySortKey(a).localeCompare(getDaySortKey(b))
    );
  }, [selectedDay]);

  const filteredDayActivities = useMemo(() => {
    if (dayTypeFilter.length === 0) return sortedDayActivities;
    return sortedDayActivities.filter((a) => a.type && dayTypeFilter.includes(a.type));
  }, [sortedDayActivities, dayTypeFilter]);

  const dayDuration = useMemo(() => computeDuration(filteredDayActivities), [filteredDayActivities]);

  const dayNotes = useMemo(
    () => filteredDayActivities.filter((a) => a.note).map((a) => a.note as string),
    [filteredDayActivities]
  );

  if (isLoading || !trip) {
    return <LoadingScreen />;
  }

  const convertToISO8601 = (daySequence: number, time: string): string => {
    if (!trip || !time) return '';
    const dayDate = addDays(trip.startDate, daySequence - 1);
    const dateStr = dayDate.toISOString().split('T')[0];
    return `${dateStr}T${time}:00`;
  };

  const handleOpenShare = async () => {
    setIsShareOpen(true);
    setShareUrl(undefined);
    setShareError(undefined);
    setShareLoading(true);
    try {
      const response = await apiPost<{ exitCode: number; data: { shareCode: string; shareUrl: string } }>(
        `/trip/${trip?.id}/share`
      );
      if (response.exitCode === 1 && response.data) {
        setShareUrl(`${window.location.origin}/share/${response.data.shareCode}`);
      } else {
        setShareError('無法產生分享連結，請稍後再試');
      }
    } catch {
      setShareError('無法產生分享連結，請稍後再試');
    } finally {
      setShareLoading(false);
    }
  };

  const handleOpenForm = () => { setEditingActivity(undefined); setIsFormOpen(true); };
  const handleCloseForm = () => { setIsFormOpen(false); setEditingActivity(undefined); };
  const handleEditActivity = (activity: Activity) => { setEditingActivity(activity); setIsFormOpen(true); };

  const convertTimeForUpdate = (time: string | undefined, activity: Activity): string | undefined => {
    if (!time || !trip) return undefined;
    const daySequence = activity.dayIndex !== null ? activity.dayIndex + 1 : 1;
    const dayDate = addDays(trip.startDate, daySequence - 1);
    const dateStr = dayDate.toISOString().split('T')[0];
    return `${dateStr} ${time}:00`;
  };

  const handleAddActivity = async (data: CreateActivityInput) => {
    if (!trip) return;
    try {
      const locationFields = {
        locationName: data.location,
        latitude: data.lat,
        longitude: data.lng,
        address: data.address,
        placeId: data.placeId,
        photoReference: data.photoReference,
      };
      if (editingActivity) {
        await itineraryService.update({
          itineraryId: editingActivity.id,
          title: data.name,
          startTime: convertTimeForUpdate(data.startTime, editingActivity),
          endTime: convertTimeForUpdate(data.endTime, editingActivity),
          note: data.note,
          type: data.type,
          ...locationFields,
        });
      } else if (data.daySequence) {
        await itineraryService.createPlanned({
          tripId: trip.id,
          title: data.name,
          daySequence: data.daySequence,
          startTime: data.startTime ? convertToISO8601(data.daySequence, data.startTime) : '',
          endTime: data.endTime ? convertToISO8601(data.daySequence, data.endTime) : '',
          note: data.note,
          type: data.type,
          ...locationFields,
        });
      } else {
        await itineraryService.createUnplanned({
          tripId: trip.id,
          title: data.name,
          note: data.note,
          type: data.type,
          ...locationFields,
        });
      }
      const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
      if (response.exitCode === 1 && response.data) setTripDetail(response.data);
      setIsFormOpen(false);
      setEditingActivity(undefined);
    } catch (error) {
      console.error('Failed to save activity:', error);
    }
  };

  const handleParseUrl = async (url: string) => {
    if (!trip) throw new Error('Trip not found');
    return itineraryService.parseUrl({ tripId: trip.id, url });
  };

  const handleDeleteActivity = async (activityId: string) => {
    try {
      await itineraryService.delete(activityId);
      const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
      if (response.exitCode === 1 && response.data) setTripDetail(response.data);
    } catch (error) {
      console.error('Failed to delete activity:', error);
    }
  };

  const handleReorderActivity = async (activityId: string, newIndex: number) => {
    if (!trip || selectedDayIndex < 0 || !selectedDay || !tripDetail) return;
    const dayActivities = selectedDay.activities;
    const oldIndex = dayActivities.findIndex((a) => a.id === activityId);
    if (oldIndex === -1) return;
    const reorderedActivities = [...dayActivities];
    const [movedActivity] = reorderedActivities.splice(oldIndex, 1);
    reorderedActivities.splice(newIndex, 0, movedActivity);
    const itineraryOrders = reorderedActivities.map((activity, index) => ({
      itineraryId: activity.id,
      orderInDay: index + 1,
    }));

    // 樂觀更新：先立刻套用新順序，避免拖拉放開後畫面先彈回舊位置、等後端回應才跳到新位置
    const newOrderByItineraryId: Record<string, number> = {};
    for (const o of itineraryOrders) {
      newOrderByItineraryId[o.itineraryId] = o.orderInDay;
    }
    const previousTripDetail = tripDetail;
    setTripDetail({
      ...tripDetail,
      plannedItineraries: tripDetail.plannedItineraries.map((itinerary) =>
        itinerary.itineraryId in newOrderByItineraryId
          ? { ...itinerary, orderInDay: newOrderByItineraryId[itinerary.itineraryId] }
          : itinerary
      ),
    });

    try {
      await itineraryService.reorder({
        tripId: trip.id,
        daySequence: selectedDayIndex + 1,
        itineraryOrders,
      });
    } catch (error) {
      console.error('Failed to reorder activity:', error);
      setTripDetail(previousTripDetail);
    }
  };

  const handleTogglePrimary = async (activity: Activity) => {
    if (!trip || !tripDetail) return;
    const newIsPrimary = !activity.isPrimary;

    // 樂觀更新：先立刻切換星星狀態，失敗再退回
    const previousTripDetail = tripDetail;
    setTripDetail({
      ...tripDetail,
      plannedItineraries: tripDetail.plannedItineraries.map((itinerary) =>
        itinerary.itineraryId === activity.id ? { ...itinerary, isPrimary: newIsPrimary } : itinerary
      ),
      unplannedItineraries: tripDetail.unplannedItineraries.map((itinerary) =>
        itinerary.itineraryId === activity.id ? { ...itinerary, isPrimary: newIsPrimary } : itinerary
      ),
    });

    try {
      await itineraryService.setPrimary({
        itineraryId: activity.id,
        tripId: trip.id,
        isPrimary: newIsPrimary,
      });
    } catch (error) {
      console.error('Failed to toggle primary:', error);
      setTripDetail(previousTripDetail);
    }
  };

  const handleMoveToDay = async (activityId: string, dayIndex: number) => {
    if (!trip) return;
    try {
      await itineraryService.updateDaySequence({
        itineraryId: activityId,
        tripId: trip.id,
        daySequence: dayIndex + 1,
      });
      const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
      if (response.exitCode === 1 && response.data) setTripDetail(response.data);
    } catch (error) {
      console.error('Failed to move activity to day:', error);
    }
  };

  const handleScheduleActivity = async (activityId: string, dayIndex: number) => {
    if (!trip) return;
    try {
      await itineraryService.updateDaySequence({
        itineraryId: activityId,
        tripId: trip.id,
        daySequence: dayIndex + 1,
      });
      const response = await apiGet<TripDetailResponse>(`/trip/${id}`);
      if (response.exitCode === 1 && response.data) setTripDetail(response.data);
    } catch (error) {
      console.error('Failed to schedule activity:', error);
    }
  };

  /** Shared tab bar */
  const tabBar = (
    <div className="flex gap-3 px-4 lg:px-10 pb-3 min-w-max">
      <button
        onClick={() => setSelectedDayIndex(-1)}
        className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 transition-all ${
          selectedDayIndex === -1
            ? 'bg-[#0dccf2] shadow-sm shadow-[#0dccf2]/20'
            : 'bg-white border border-slate-200 hover:border-[#0dccf2]/50'
        }`}
      >
        <svg
          className={`w-[18px] h-[18px] ${selectedDayIndex === -1 ? 'text-white' : 'text-slate-600'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
        <span className={`text-sm font-bold leading-normal ${selectedDayIndex === -1 ? 'text-white' : 'text-slate-600'}`}>
          總覽
        </span>
      </button>
      {Array.from({ length: days.length }, (_, i) => (
        <button
          key={i}
          onClick={() => setSelectedDayIndex(i)}
          className={`flex h-9 shrink-0 items-center justify-center gap-x-2 rounded-full px-4 transition-colors ${
            selectedDayIndex === i
              ? 'bg-[#0dccf2] text-white'
              : 'bg-white border border-slate-200 hover:border-[#0dccf2]/50'
          }`}
        >
          <span className={`text-sm font-medium leading-normal ${selectedDayIndex === i ? 'text-white' : 'text-slate-600'}`}>
            Day {i + 1}
          </span>
        </button>
      ))}
    </div>
  );

  // 手機版與桌面版共用的 Overview 地圖。兩種版面同時存在 DOM 裡（靠 CSS 切換），
  // 所以呼叫端只在當下版面渲染一份，避免同時載入兩個 Google Map 實例
  const overviewMap = (mapClassName: string) => (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-900">地圖</h3>
        <div className="flex rounded-full bg-slate-100 p-0.5">
          {([['all', '全部行程'], ['unscheduled', '未排期']] as const).map(([scope, label]) => (
            <button
              key={scope}
              type="button"
              onClick={() => {
                setOverviewMapScope(scope);
                setOverviewMapActivityId(null);
              }}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                overviewMapScope === scope ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className={`rounded-2xl overflow-hidden border border-slate-100 ${mapClassName}`}>
        <TripMapView
          activities={overviewMapActivities}
          tripId={trip?.id}
          selectedActivityId={overviewMapActivityId}
          onMarkerClick={(activityId) => setOverviewMapActivityId(activityId || null)}
          onMapClick={() => setOverviewMapActivityId(null)}
          showRoute={false}
        />
      </div>
      {overviewMapScope === 'all' && (
        <div className="flex items-center justify-end gap-4 mt-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-3 rounded-full bg-[#0dccf2] border-2 border-white shadow" />
            已排期
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block size-3 rounded-full bg-white border-[3px] border-[#0dccf2] shadow" />
            未排期
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-dvh bg-[#f8f9fa]">
      {/* ── Desktop Sidebar ── */}
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      {/* ── Main content column ── */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">

        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-50 bg-background-light/95 backdrop-blur-sm border-b border-slate-200">
          <div className="flex items-center p-4 justify-between">
            <button
              onClick={() => navigate('/trips')}
              className="text-slate-900 flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
              {trip.name}
            </h1>
            <div className="flex items-center">
              <button onClick={handleOpenShare} className="flex w-10 items-center justify-center rounded-full hover:bg-slate-100 transition-colors">
                <svg className="w-6 h-6 text-[#0dccf2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              <button
                onClick={() => setShowMenuSheet(true)}
                className="flex w-10 items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              >
                <MoreVertical size={22} className="text-slate-900" />
              </button>
            </div>
          </div>
          <p className="text-slate-500 text-sm font-medium leading-normal pb-4 text-center">
            {formatDateRange(trip.startDate, trip.endDate)}
          </p>
          {!isFormOpen && (
            <div className="w-full overflow-x-auto scrollbar-hide border-b border-slate-200 bg-background-light">
              {tabBar}
            </div>
          )}
        </header>

        {/* ─── Desktop Header (hidden when form is open) ─── */}
        <header className={`${isFormOpen ? 'hidden' : 'hidden lg:flex'} flex-col bg-white border-b border-slate-100 sticky top-0 z-40`}>
          <div className="flex items-center justify-between px-10 py-5">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/trips')}
                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                返回
              </button>
              <div>
                <h1 className="text-slate-900 text-xl font-bold leading-tight">{trip.name}</h1>
                <p className="text-slate-400 text-sm mt-0.5">
                  {formatDateRange(trip.startDate, trip.endDate)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleOpenShare}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-sm font-medium"
              >
                <Share2 size={15} />
                分享
              </button>
              <button
                onClick={handleOpenForm}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0dccf2] text-white hover:bg-[#0dccf2]/90 transition-colors text-sm font-bold shadow-sm shadow-[#0dccf2]/20"
              >
                <Plus size={15} />
                新增行程
              </button>
            </div>
          </div>
          <div className="overflow-x-auto scrollbar-hide border-t border-slate-100">
            <div className="pt-3">{tabBar}</div>
          </div>
        </header>

        {/* ─── Mobile Content ─── */}
        <div className="lg:hidden flex-1 flex flex-col min-h-0">
          {selectedDayIndex === -1 ? (
            /* Mobile Overview */
            <main className="flex-1 w-full max-w-md mx-auto">
              <div className="px-4 pt-6 pb-2 flex justify-between items-end">
                <div>
                  <h2 className="text-slate-900 text-2xl font-bold leading-tight tracking-tight">總覽</h2>
                  <p className="text-slate-500 text-sm font-medium">
                    未排期行程 • {filteredUnscheduledActivities.length} 個
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ActivityTypeFilter selectedTypes={unscheduledTypeFilter} onChange={setUnscheduledTypeFilter} />
                  {/* 排序按鈕先隱藏，待排序功能實作後再加回 */}
                </div>
              </div>
              {!isDesktop && <div className="px-4 pt-4">{overviewMap('h-[280px]')}</div>}
              <div className="flex flex-col px-4 pt-4">
                {filteredUnscheduledActivities.map((activity) => (
                  <div
                    key={activity.id}
                    onClick={() => navigate(`/trips/${id}/itinerary/${activity.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <ActivityCard
                      activity={activity}
                      variant="unscheduled"
                      onEdit={() => handleEditActivity(activity)}
                      onDelete={() => setDeletingActivity(activity)}
                      onSchedule={(dayIndex) => handleScheduleActivity(activity.id, dayIndex)}
                      availableDays={days}
                    />
                  </div>
                ))}
              </div>
            </main>
          ) : selectedDay ? (
            /* Mobile Day View — Map + Bottom Sheet（只在手機版渲染，避免和桌面版地圖同時載入） */
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              {!isDesktop && <MapBottomSheet
                day={selectedDay}
                onEditActivity={handleEditActivity}
                onDeleteActivity={handleDeleteActivity}
                onReorderActivity={handleReorderActivity}
                onMoveToDay={handleMoveToDay}
                onTogglePrimary={handleTogglePrimary}
                availableDays={days}
                showRoute={showMapRoute}
                onShowRouteChange={setShowMapRoute}
              />}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">找不到這一天的資料</div>
          )}
        </div>

        {/* ─── Desktop Content ─── */}
        <div className="hidden lg:flex flex-1 min-h-0">
          {isFormOpen ? (
            /* Desktop Add/Edit Form — two-panel layout */
            <div className="flex flex-col flex-1 min-h-0 bg-[#f8f9fa]">
                {/* Top Bar */}
                <div className="flex items-center gap-4 px-10 py-5 bg-white border-b border-slate-100 shrink-0">
                  <button
                    onClick={handleCloseForm}
                    className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors shrink-0"
                  >
                    <ArrowLeft size={18} />
                    返回
                  </button>
                  <h1 className="text-slate-900 text-[22px] font-bold leading-tight">
                    {editingActivity ? '編輯行程' : '新增行程'}
                  </h1>
                  {trip && (
                    <div className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5">
                      <Map size={14} className="text-slate-400 shrink-0" />
                      <span className="text-slate-500 text-sm">{trip.name}</span>
                    </div>
                  )}
                </div>

                {/* Body: Form + Preview */}
                <div className="flex flex-1 min-h-0">
                  {/* Form Panel */}
                  <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white">
                    {/* ActivityForm content (scrollable) */}
                    <div className="flex-1 overflow-y-auto">
                      <ActivityForm
                        ref={activityFormRef}
                        activity={editingActivity}
                        tripId={trip!.id}
                        tripStartDate={trip!.startDate}
                        maxDays={days.length}
                        defaultDaySequence={selectedDayIndex >= 0 ? selectedDayIndex + 1 : undefined}
                        isEditMode={!!editingActivity}
                        tripName={trip?.name}
                        desktopMode
                        onSubmit={handleAddActivity}
                        onParseUrl={handleParseUrl}
                      />
                    </div>
                    {/* Desktop Buttons */}
                    <div className="shrink-0 flex items-center gap-3 px-12 py-6 border-t border-slate-100 bg-white">
                      <button
                        type="button"
                        onClick={handleCloseForm}
                        className="flex items-center justify-center rounded-xl border border-slate-200 px-7 py-3.5 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        onClick={() => activityFormRef.current?.submit()}
                        className="flex items-center justify-center rounded-xl bg-[#0dccf2] px-9 py-3.5 text-sm font-semibold text-white hover:bg-[#0bbfde] transition-colors"
                      >
                        {editingActivity ? '更新' : '新增'}
                      </button>
                    </div>
                  </div>

                  {/* Preview Panel */}
                  <aside className="w-[420px] shrink-0 flex flex-col gap-6 overflow-y-auto border-l border-slate-100 bg-white px-7 py-8">
                    <div>
                      <h2 className="text-slate-900 text-lg font-bold mb-1">預覽</h2>
                      <p className="text-[#999] text-sm leading-normal">預覽這個行程在列表中的樣子</p>
                    </div>

                    {/* Preview Card */}
                    <div className="flex items-center gap-3.5 rounded-2xl border border-[#E8E8E8] bg-white px-4 py-4 shadow-sm">
                      <div className="flex items-center justify-center rounded-full bg-[#E0F7FA] shrink-0" style={{ width: 44, height: 44 }}>
                        <PersonStanding size={22} className="text-[#0dccf2]" />
                      </div>
                      <div className="flex flex-col gap-1.5 min-w-0">
                        <span className="text-[#0dccf2] text-xs font-bold">--:-- --</span>
                        <span className="text-[#CCC] text-base font-semibold">行程名稱</span>
                        <span className="text-[#CCC] text-sm">地點 • 詳細資訊</span>
                      </div>
                    </div>

                    <div className="h-px bg-[#F0F0F0]" />

                    <div className="flex flex-col gap-3">
                      <h3 className="text-slate-900 text-[15px] font-semibold">小提醒</h3>
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start gap-2.5">
                          <Lightbulb size={16} className="text-amber-500 mt-0.5 shrink-0" />
                          <p className="text-[#6D6C6A] text-sm leading-normal">加上明確的時間，讓行程更有條理</p>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <MapPin size={16} className="text-[#0dccf2] mt-0.5 shrink-0" />
                          <p className="text-[#6D6C6A] text-sm leading-normal">填寫地址，出門時就能直接導航</p>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <Clipboard size={16} className="text-violet-500 mt-0.5 shrink-0" />
                          <p className="text-[#6D6C6A] text-sm leading-normal">用備註記下訂位代號或特殊注意事項</p>
                        </div>
                      </div>
                    </div>
                  </aside>
                </div>
            </div>
          ) : selectedDayIndex === -1 ? (
            /* Desktop Overview — 左欄卡片 + 右欄地圖 */
            <>
            <main className="flex-1 min-w-0 overflow-y-auto px-10 py-8">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-slate-900 text-2xl font-bold">總覽</h2>
                  <p className="text-slate-500 text-sm mt-1">
                    未排期行程 • {filteredUnscheduledActivities.length} 個
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ActivityTypeFilter selectedTypes={unscheduledTypeFilter} onChange={setUnscheduledTypeFilter} />
                  {/* 排序按鈕先隱藏，待排序功能實作後再加回 */}
                </div>
              </div>
              {filteredUnscheduledActivities.length > 0 ? (
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredUnscheduledActivities.map((activity) => {
                    const cfg = getActivityTypeConfig(activity.type);
                    const photoUrl = getPlacePhotoUrl(activity.placeId);
                    return (
                      <div
                        key={activity.id}
                        className="rounded-2xl overflow-hidden bg-white border border-[#E8E8E8] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col"
                        style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}
                        onClick={() => navigate(`/trips/${id}/itinerary/${activity.id}`)}
                      >
                        {/* Photo */}
                        <div className="h-40 w-full shrink-0 overflow-hidden bg-slate-100">
                          {photoUrl ? (
                            <img src={photoUrl} alt={activity.name} className="w-full h-full object-cover" />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{ background: cfg.bgColor }}
                            >
                              <svg width="36" height="36" viewBox="0 0 24 24" fill={cfg.iconColor} opacity={0.4}>
                                <path d={cfg.icon} />
                              </svg>
                            </div>
                          )}
                        </div>
                        {/* Body */}
                        <div className="flex flex-col gap-2.5 p-4">
                          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            {days.length > 0 && (
                              <Dropdown
                                align="right"
                                trigger={
                                  <button
                                    className="flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors shrink-0"
                                    style={{ width: 28, height: 28 }}
                                    title="排入行程"
                                  >
                                    <CalendarPlus size={14} className="text-slate-600" />
                                  </button>
                                }
                                options={days.map((day) => ({
                                  value: day.dayIndex,
                                  label: `Day ${day.dayNumber} · ${formatDateShort(day.date)}`,
                                }))}
                                onSelect={(dayIndex) => handleScheduleActivity(activity.id, dayIndex as number)}
                              />
                            )}
                            <button
                              onClick={() => setDeletingActivity(activity)}
                              className="flex items-center justify-center rounded-full bg-slate-100 hover:bg-red-50 hover:text-red-600 transition-colors shrink-0 text-slate-600"
                              style={{ width: 28, height: 28 }}
                              title="刪除行程"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="flex items-start gap-3.5">
                            <div
                              className="shrink-0 flex items-center justify-center rounded-full"
                              style={{ width: 44, height: 44, background: cfg.bgColor }}
                            >
                              <svg width="22" height="22" viewBox="0 0 24 24" fill={cfg.iconColor}>
                                <path d={cfg.icon} />
                              </svg>
                            </div>
                            <div className="flex flex-col gap-1.5 min-w-0">
                              <span className="text-[11px] font-semibold" style={{ color: cfg.iconColor }}>
                                {cfg.label}
                              </span>
                              <h3 className="text-base font-semibold text-slate-900 leading-tight">{activity.name}</h3>
                              {activity.location && (
                                <p className="text-[13px] text-[#999] leading-normal truncate">{activity.location}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <p className="text-sm">
                    {unscheduledActivities.length > 0 ? '沒有符合篩選條件的行程' : '尚無未排期行程'}
                  </p>
                </div>
              )}
            </main>
            {isDesktop && (
              <aside className="w-[480px] shrink-0 flex flex-col border-l border-slate-100 bg-white px-6 py-8">
                {overviewMap('flex-1 min-h-[360px]')}
              </aside>
            )}
            </>
          ) : selectedDay ? (
            /* Desktop Day View — two-column layout */
            <>
              {/* Left column: activity timeline */}
              <main className="flex-1 min-w-0 overflow-y-auto px-10 py-8">
                {/* Day heading */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-slate-900 text-2xl font-bold leading-tight">
                      Day {selectedDay.dayNumber}
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                      {formatDateWithDay(selectedDay.date)}
                      {filteredDayActivities.length > 0 && ` • ${filteredDayActivities.length} 個行程`}
                    </p>
                  </div>
                  <ActivityTypeFilter selectedTypes={dayTypeFilter} onChange={setDayTypeFilter} />
                </div>

                {/* Timeline */}
                {filteredDayActivities.length > 0 ? (
                  <ActivityList
                    activities={filteredDayActivities}
                    onEditActivity={handleEditActivity}
                    onDeleteActivity={(activity) => setDeletingActivity(activity)}
                    onReorderActivity={handleReorderActivity}
                    onMoveToDay={handleMoveToDay}
                    onSelectActivity={(activity) => navigate(`/trips/${id}/itinerary/${activity.id}?day=${selectedDayIndex + 1}`)}
                    onTogglePrimary={handleTogglePrimary}
                    availableDays={days}
                    disableDrag={dayTypeFilter.length > 0}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <svg className="w-10 h-10 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-sm">
                      {sortedDayActivities.length > 0 ? '沒有符合篩選條件的行程' : '這天還沒有安排行程'}
                    </p>
                  </div>
                )}

                {/* Add to day */}
                <button
                  onClick={handleOpenForm}
                  className="mt-4 flex items-center gap-2 text-slate-400 hover:text-[#0dccf2] transition-colors text-sm font-medium group"
                >
                  <div className="w-9 h-9 rounded-full border-2 border-dashed border-slate-200 group-hover:border-[#0dccf2] flex items-center justify-center transition-colors">
                    <Plus size={14} />
                  </div>
                  新增行程至 Day {selectedDay.dayNumber}
                </button>
              </main>

              {/* Right column: summary panel */}
              <aside className="w-[34rem] shrink-0 border-l border-slate-100 bg-white overflow-y-auto">
                <div className="p-6 space-y-6">
                  {/* Day Summary */}
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 mb-3">當日摘要</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#f0fbff] rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-[#0dccf2]">{filteredDayActivities.length}</p>
                        <p className="text-xs text-slate-500 mt-0.5">行程</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-slate-700">{dayDuration}</p>
                        <p className="text-xs text-slate-500 mt-0.5">總時長</p>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-slate-900">地圖</h3>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <span className="text-xs text-slate-500">顯示順序與路線</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={showMapRoute}
                          onClick={() => setShowMapRoute((v) => !v)}
                          className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
                          style={{ background: showMapRoute ? '#0dccf2' : '#E5E7EB' }}
                        >
                          <span
                            className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform"
                            style={{ transform: showMapRoute ? 'translateX(18px)' : 'translateX(3px)' }}
                          />
                        </button>
                      </label>
                    </div>
                    <div className="rounded-xl overflow-hidden" style={{ height: 420 }}>
                      {isDesktop && (
                        <TripMapView
                          activities={filteredDayActivities}
                          tripId={trip.id}
                          selectedActivityId={selectedActivityId}
                          onMarkerClick={(id) => setSelectedActivityId(id || null)}
                          onMapClick={() => setSelectedActivityId(null)}
                          showRoute={showMapRoute}
                        />
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {dayNotes.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 mb-2">備註</h3>
                      <ul className="space-y-1.5">
                        {dayNotes.map((note, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                            <span className="text-[#0dccf2] mt-0.5 shrink-0">•</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </aside>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">找不到這一天的資料</div>
          )}
        </div>
      </div>

      {/* Mobile Floating Add Button */}
      <button
        onClick={handleOpenForm}
        className="lg:hidden fixed bottom-6 right-6 z-50 flex size-14 items-center justify-center rounded-full bg-[#0dccf2] text-white shadow-lg shadow-[#0dccf2]/30 hover:bg-[#0dccf2]/90 transition-all hover:scale-105 active:scale-95"
      >
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* Add/Edit Activity Modal (mobile only — desktop uses inline panel) */}
      <div className="lg:hidden">
      {trip && (
        <Modal
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          title={editingActivity ? "編輯行程" : "新增行程"}
          variant="bottom-sheet"
        >
          <ActivityForm
            activity={editingActivity}
            tripId={trip.id}
            tripStartDate={trip.startDate}
            maxDays={days.length}
            defaultDaySequence={selectedDayIndex >= 0 ? selectedDayIndex + 1 : undefined}
            isEditMode={!!editingActivity}
            tripName={trip.name}
            onSubmit={handleAddActivity}
            onParseUrl={handleParseUrl}
          />
        </Modal>
      )}
      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        shareUrl={shareUrl}
        isLoading={shareLoading}
        error={shareError}
      />

      {id && (
        <TripMenuSheet isOpen={showMenuSheet} onClose={() => setShowMenuSheet(false)} tripId={id} />
      )}

      <ConfirmDialog
        isOpen={!!deletingActivity}
        title="刪除行程"
        message={`確定要刪除「${deletingActivity?.name}」嗎？`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={() => {
          if (deletingActivity) {
            handleDeleteActivity(deletingActivity.id);
            setDeletingActivity(undefined);
          }
        }}
        onClose={() => setDeletingActivity(undefined)}
      />

      {/* Mobile bottom gradient */}
      <div className="lg:hidden fixed bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent opacity-50" />
    </div>
  );
}
